import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import type { AiEmailPrompt } from "@/lib/types";

export function EmailComposer({
  contactId,
  toEmail,
  onClose,
  onSent,
}: {
  contactId: string;
  toEmail: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const [prompts, setPrompts] = useState<AiEmailPrompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [selectedPromptId, setSelectedPromptId] = useState<string | "">("");
  const [customPrompt, setCustomPrompt] = useState("");

  const [manageOpen, setManageOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editText, setEditText] = useState("");

  const loadPrompts = useCallback(async () => {
    setPromptsLoading(true);
    try {
      const res = await fetch("/api/ai-prompts", { credentials: "include" });
      const data = await res.json();
      setPrompts(data.prompts ?? []);
    } catch {
      /* silent */
    } finally {
      setPromptsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  function getActivePromptText(): string {
    if (selectedPromptId) {
      const p = prompts.find((p) => p.id === selectedPromptId);
      return p?.prompt_text ?? "";
    }
    return customPrompt;
  }

  async function generateWithAi() {
    const promptText = getActivePromptText();
    if (!promptText.trim()) return;

    setAiGenerating(true);
    setAiError(null);
    try {
      const res = await fetch("/api/ai-prompts/compose", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contact_id: contactId, prompt_text: promptText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "AI generation failed");
      setSubject(data.subject);
      setBody(data.body);
      setAiOpen(false);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "AI generation failed");
    } finally {
      setAiGenerating(false);
    }
  }

  async function saveNewPrompt() {
    if (!newName.trim() || !newText.trim()) return;
    setSavingPrompt(true);
    try {
      const res = await fetch("/api/ai-prompts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName, prompt_text: newText }),
      });
      if (res.ok) {
        const data = await res.json();
        setPrompts((ps) => [data.prompt, ...ps]);
        setNewName("");
        setNewText("");
      }
    } finally {
      setSavingPrompt(false);
    }
  }

  async function updatePrompt(id: string) {
    if (!editName.trim() || !editText.trim()) return;
    const res = await fetch(`/api/ai-prompts/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editName, prompt_text: editText }),
    });
    if (res.ok) {
      const data = await res.json();
      setPrompts((ps) => ps.map((p) => (p.id === id ? data.prompt : p)));
      setEditingId(null);
    }
  }

  async function deletePrompt(id: string) {
    const res = await fetch(`/api/ai-prompts/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      setPrompts((ps) => ps.filter((p) => p.id !== id));
      if (selectedPromptId === id) setSelectedPromptId("");
    }
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const html = body
        .split("\n\n")
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("");
      const res = await fetch("/api/email/send", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_id: contactId,
          to: toEmail,
          subject,
          body_html: html,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Send failed");
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-xl border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-base font-semibold">New Email</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={send} className="space-y-4 p-6">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              To
            </label>
            <div className="mt-1 rounded-md border bg-slate-50 px-3 py-2 text-sm">
              {toEmail}
            </div>
          </div>

          {/* Write with AI section */}
          <div className="rounded-lg border border-violet-200 bg-violet-50/50">
            <button
              type="button"
              onClick={() => setAiOpen(!aiOpen)}
              className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-100/50"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Write with AI
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${aiOpen ? "rotate-180" : ""}`}
              />
            </button>

            {aiOpen && (
              <div className="space-y-3 border-t border-violet-200 px-4 py-3">
                {/* Prompt selector */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-xs font-medium text-violet-600">
                      Prompt
                    </label>
                    <button
                      type="button"
                      onClick={() => setManageOpen(true)}
                      className="text-xs font-medium text-violet-600 hover:text-violet-800"
                    >
                      Manage prompts
                    </button>
                  </div>

                  {promptsLoading ? (
                    <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading prompts...
                    </div>
                  ) : (
                    <>
                      {prompts.length > 0 && (
                        <select
                          value={selectedPromptId}
                          onChange={(e) => {
                            setSelectedPromptId(e.target.value);
                            if (e.target.value) setCustomPrompt("");
                          }}
                          className="mb-2 w-full rounded-md border bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                        >
                          <option value="">Write a custom prompt...</option>
                          {prompts.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      )}

                      {!selectedPromptId && (
                        <textarea
                          rows={3}
                          value={customPrompt}
                          onChange={(e) => setCustomPrompt(e.target.value)}
                          placeholder='e.g. "Write a friendly intro email about our social media management platform, mention their company and how we can help them grow."'
                          className="w-full rounded-md border bg-white px-3 py-2 text-sm outline-none placeholder:text-muted-foreground/60 focus:ring-2 focus:ring-violet-300"
                        />
                      )}

                      {selectedPromptId && (
                        <div className="rounded-md border bg-white px-3 py-2 text-xs text-muted-foreground">
                          {prompts.find((p) => p.id === selectedPromptId)
                            ?.prompt_text ?? ""}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {aiError && (
                  <p className="text-xs text-destructive">{aiError}</p>
                )}

                <button
                  type="button"
                  onClick={generateWithAi}
                  disabled={
                    aiGenerating || !getActivePromptText().trim()
                  }
                  className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {aiGenerating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {aiGenerating ? "Generating..." : "Generate Email"}
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Subject
            </label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Body
            </label>
            <textarea
              required
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your email. Use http:// URLs to track clicks."
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              A tracking pixel will be added and all links wrapped for click
              tracking.
            </p>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !subject || !body}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send
            </button>
          </div>
        </form>
      </div>

      {/* Manage Prompts Modal */}
      {manageOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-base font-semibold">Saved AI Prompts</h3>
              <button
                type="button"
                onClick={() => setManageOpen(false)}
                className="rounded p-1 text-muted-foreground hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-6">
              {/* Add new prompt */}
              <div className="mb-4 space-y-2 rounded-lg border border-dashed border-violet-300 bg-violet-50/30 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-600">
                  New Prompt
                </h4>
                <input
                  type="text"
                  placeholder="Prompt name (e.g. Cold Intro)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                />
                <textarea
                  rows={3}
                  placeholder="Prompt instructions for the AI..."
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  className="w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                />
                <button
                  type="button"
                  onClick={saveNewPrompt}
                  disabled={
                    savingPrompt || !newName.trim() || !newText.trim()
                  }
                  className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {savingPrompt ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  Save Prompt
                </button>
              </div>

              {/* Existing prompts */}
              {prompts.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No saved prompts yet. Create one above to get started.
                </p>
              ) : (
                <div className="space-y-2">
                  {prompts.map((p) => (
                    <div
                      key={p.id}
                      className="rounded-lg border bg-white p-3"
                    >
                      {editingId === p.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                          />
                          <textarea
                            rows={3}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full rounded-md border px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-300"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => updatePrompt(p.id)}
                              className="rounded-md bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-700"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-slate-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-1 flex items-start justify-between">
                            <span className="text-sm font-medium">
                              {p.name}
                            </span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingId(p.id);
                                  setEditName(p.name);
                                  setEditText(p.prompt_text);
                                }}
                                className="rounded p-1 text-muted-foreground hover:bg-slate-100 hover:text-foreground"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => deletePrompt(p.id)}
                                className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs leading-relaxed text-muted-foreground">
                            {p.prompt_text}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
